import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChevronRight, ChevronLeft, CheckCircle, TrendingUp, Shield, Target, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "info", title: "Información Financiera" },
  { id: "knowledge", title: "Conocimiento Financiero" },
  { id: "goals", title: "Objetivos" },
  { id: "challenges", title: "Retos" },
  { id: "risk", title: "Tolerancia al Riesgo" },
];

const KNOWLEDGE_LEVELS = [
  { value: "muy_bajo", label: "Muy Bajo", desc: "No manejo conceptos financieros. No sé qué es un presupuesto o tasa de interés." },
  { value: "bajo", label: "Bajo", desc: "Entiendo lo básico: ingresos, gastos y ahorro. No conozco instrumentos de inversión." },
  { value: "intermedio", label: "Intermedio", desc: "Manejo presupuestos, conozco créditos, tasas y tengo nociones de inversión." },
  { value: "avanzado", label: "Avanzado", desc: "Conozco CDTs, fondos, acciones, diversificación y análisis de riesgo-retorno." },
  { value: "muy_alto", label: "Muy Alto", desc: "Domino análisis financiero, portafolios, derivados, valoración de activos y gestión patrimonial." },
];

const KNOWLEDGE_AREAS = [
  "Presupuesto personal", "Manejo de deudas", "Ahorro programado",
  "Tasas de interés", "CDTs y renta fija", "Fondos de inversión",
  "Acciones y renta variable", "Criptomonedas", "Bienes raíces",
  "Análisis técnico/fundamental", "Diversificación de portafolio",
  "Planeación tributaria", "Seguros y protección", "Pensiones y retiro",
];

const GOALS = [
  { value: "eliminar_deudas", label: "Eliminar deudas" },
  { value: "fondo_emergencia", label: "Crear fondo de emergencia" },
  { value: "ahorro_vivienda", label: "Ahorro para vivienda" },
  { value: "invertir", label: "Comenzar a invertir" },
  { value: "incrementar_patrimonio", label: "Incrementar patrimonio" },
  { value: "independencia_financiera", label: "Independencia financiera" },
  { value: "educacion", label: "Educación propia o familiar" },
  { value: "emprendimiento", label: "Emprendimiento / negocio" },
  { value: "retiro", label: "Planificar el retiro" },
  { value: "no_se", label: "No sé aún" },
  { value: "otro", label: "Otro" },
];

const CHALLENGES = [
  { value: "no_ahorro", label: "No logro ahorrar" },
  { value: "gastos_hormiga", label: "Gastos hormiga descontrolados" },
  { value: "deudas", label: "Deudas acumuladas" },
  { value: "ingresos_bajos", label: "Ingresos insuficientes" },
  { value: "no_presupuesto", label: "No sé hacer presupuesto" },
  { value: "no_herramientas", label: "Desconozco herramientas financieras" },
  { value: "no_inversion", label: "No sé cómo invertir" },
  { value: "riesgo", label: "Miedo al riesgo" },
  { value: "falta_disciplina", label: "Falta de disciplina financiera" },
  { value: "analisis_mercado", label: "No entiendo el análisis de mercado" },
  { value: "diversificacion", label: "No sé diversificar mi portafolio" },
  { value: "impuestos", label: "Optimización tributaria" },
];

export default function Survey() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [form, setForm] = useState({
    monthly_income: "", monthly_expenses: "", current_savings: "", total_debt: "",
    financial_knowledge: "", knowledge_details: [],
    main_financial_goal: "", goal_timeframe: "", goal_other_detail: "",
    biggest_challenge: "", challenge_details: [],
    risk_tolerance: "moderado", investment_experience: "ninguna"
  });

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const toggleArray = (key, val) => {
    setForm(p => {
      const arr = p[key] || [];
      return { ...p, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const canNext = () => {
    if (step === 0) return form.monthly_income && form.monthly_expenses;
    if (step === 1) return form.financial_knowledge;
    if (step === 2) return form.main_financial_goal;
    if (step === 3) return form.biggest_challenge;
    if (step === 4) return form.risk_tolerance;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_income: parseFloat(form.monthly_income) || 0,
        monthly_expenses: parseFloat(form.monthly_expenses) || 0,
        current_savings: parseFloat(form.current_savings) || 0,
        total_debt: parseFloat(form.total_debt) || 0,
      };
      const r = await axios.post(`${API}/survey`, payload, { withCredentials: true });
      setDiagnosis(r.data);
    } catch (e) {
      toast.error("Error al enviar encuesta");
    } finally {
      setSaving(false);
    }
  };

  const goToDashboard = async () => {
    await checkAuth();
    navigate("/dashboard", { replace: true });
  };

  // Diagnosis result screen
  if (diagnosis) {
    const levelColors = { principiante: "text-blue-400", avanzado: "text-[#D4AF37]", maestro: "text-emerald-400" };
    const levelLabels = { principiante: "Principiante", avanzado: "Avanzado", maestro: "Maestro Financiero" };
    const riskColors = { conservador: "text-emerald-400", moderado: "text-[#D4AF37]", agresivo: "text-red-400" };

    return (
      <div className="min-h-screen bg-[#0f1724] flex items-center justify-center p-4" data-testid="survey-diagnosis">
        <div className="max-w-lg w-full space-y-6">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Tu Diagnóstico Financiero</h1>
          </div>
          <Card className="bg-[#1a2332] border-[#2a3444]">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#141b2d] rounded-lg">
                <TrendingUp className={`w-10 h-10 ${levelColors[diagnosis.financial_level]}`} />
                <div>
                  <p className="text-sm text-gray-400">Tu Nivel Financiero</p>
                  <p className={`text-2xl font-bold ${levelColors[diagnosis.financial_level]}`}>{levelLabels[diagnosis.financial_level]}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300">{diagnosis.diagnosis}</p>
              <div className="flex items-center gap-4 p-4 bg-[#141b2d] rounded-lg">
                <Shield className={`w-10 h-10 ${riskColors[diagnosis.risk_profile]}`} />
                <div>
                  <p className="text-sm text-gray-400">Perfil de Riesgo</p>
                  <p className={`text-xl font-bold capitalize ${riskColors[diagnosis.risk_profile]}`}>{diagnosis.risk_profile}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300">{diagnosis.risk_description}</p>
            </CardContent>
          </Card>
          <Button onClick={goToDashboard} className="w-full btn-gold py-3 text-lg" data-testid="go-to-dashboard">
            Ir al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const selectedKnowledge = KNOWLEDGE_LEVELS.find(k => k.value === form.financial_knowledge);

  return (
    <div className="min-h-screen bg-[#0f1724] flex items-center justify-center p-4" data-testid="survey-page">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Conoce tu Perfil Financiero y como Inversionista
          </h1>
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i < step ? 'bg-[#D4AF37] text-[#141b2d]' : i === step ? 'bg-[#D4AF37]/30 text-[#D4AF37] border border-[#D4AF37]' : 'bg-[#2a3444] text-gray-600'
                }`}>{i + 1}</div>
                {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? 'bg-[#D4AF37]' : 'bg-[#2a3444]'}`} />}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-2">{STEPS[step].title}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-3">
            <Card className="bg-[#1a2332] border-[#2a3444]">
              <CardContent className="p-6">
                {/* Step 0: Financial Info */}
                {step === 0 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-white mb-4">Información Financiera Básica</h2>
                    {[
                      { key: "monthly_income", label: "Ingresos mensuales (COP)" },
                      { key: "monthly_expenses", label: "Gastos mensuales (COP)" },
                      { key: "current_savings", label: "Ahorros actuales (COP)" },
                      { key: "total_debt", label: "Deuda total (COP)" },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-sm text-gray-400 mb-1 block">{f.label}</label>
                        <input type="number" value={form[f.key]} onChange={e => update(f.key, e.target.value)}
                          className="w-full bg-[#141b2d] border border-[#2a3444] text-white rounded-lg px-4 py-3 focus:border-[#D4AF37] outline-none"
                          data-testid={`survey-${f.key}`} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Step 1: Knowledge */}
                {step === 1 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-white mb-2">Nivel de Conocimiento Financiero</h2>
                    <p className="text-xs text-gray-500 mb-3">Selecciona tu nivel y las áreas que conoces</p>
                    <div className="space-y-2">
                      {KNOWLEDGE_LEVELS.map(k => (
                        <button key={k.value} onClick={() => update("financial_knowledge", k.value)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            form.financial_knowledge === k.value ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-white' : 'bg-[#141b2d] border-[#2a3444] text-gray-400 hover:border-[#D4AF37]/50'
                          }`} data-testid={`knowledge-${k.value}`}>
                          <span className="font-medium">{k.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 mb-2">¿En qué áreas tienes conocimiento?</p>
                      <div className="grid grid-cols-2 gap-2">
                        {KNOWLEDGE_AREAS.map(area => (
                          <button key={area} onClick={() => toggleArray("knowledge_details", area)}
                            className={`text-xs p-2 rounded-lg border transition-all text-left ${
                              (form.knowledge_details || []).includes(area) ? 'bg-[#D4AF37]/10 border-[#D4AF37]/50 text-[#D4AF37]' : 'bg-[#141b2d] border-[#2a3444] text-gray-500 hover:text-gray-300'
                            }`}>{area}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Goals */}
                {step === 2 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-white mb-2">Objetivos Financieros</h2>
                    <div className="space-y-2">
                      {GOALS.map(g => (
                        <button key={g.value} onClick={() => update("main_financial_goal", g.value)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            form.main_financial_goal === g.value ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-white' : 'bg-[#141b2d] border-[#2a3444] text-gray-400 hover:border-[#D4AF37]/50'
                          }`}>{g.label}</button>
                      ))}
                    </div>
                    {form.main_financial_goal === "otro" && (
                      <input type="text" placeholder="Especifica tu objetivo..." value={form.goal_other_detail}
                        onChange={e => update("goal_other_detail", e.target.value)}
                        className="w-full bg-[#141b2d] border border-[#D4AF37]/50 text-white rounded-lg px-4 py-3 focus:border-[#D4AF37] outline-none" />
                    )}
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 mb-2">¿En qué plazo?</p>
                      <div className="grid grid-cols-4 gap-2">
                        {[{v:"1_year",l:"1 año"},{v:"3_years",l:"3 años"},{v:"5_years",l:"5 años"},{v:"no_se",l:"No sé"}].map(t => (
                          <button key={t.v} onClick={() => update("goal_timeframe", t.v)}
                            className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                              form.goal_timeframe === t.v ? 'bg-[#D4AF37] text-[#141b2d] border-[#D4AF37]' : 'bg-[#141b2d] border-[#2a3444] text-gray-400'
                            }`}>{t.l}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Challenges */}
                {step === 3 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-white mb-2">Retos Financieros</h2>
                    <p className="text-xs text-gray-500 mb-3">Selecciona tu mayor reto y marca todos los que apliquen</p>
                    <div className="space-y-2">
                      {CHALLENGES.map(c => (
                        <button key={c.value} onClick={() => { update("biggest_challenge", c.value); toggleArray("challenge_details", c.value); }}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            form.biggest_challenge === c.value ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-white' : (form.challenge_details || []).includes(c.value) ? 'bg-[#2a3444]/50 border-[#D4AF37]/30 text-gray-300' : 'bg-[#141b2d] border-[#2a3444] text-gray-400 hover:border-[#D4AF37]/50'
                          }`}>{c.label}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Risk */}
                {step === 4 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-white mb-2">Tolerancia al Riesgo</h2>
                    {[
                      { v: "conservador", l: "Conservador", d: "Prefiero seguridad. Retornos bajos pero estables. No quiero perder capital.", c: "text-emerald-400 border-emerald-400/50 bg-emerald-500/10" },
                      { v: "moderado", l: "Moderado", d: "Busco balance. Acepto algo de riesgo por mejor rentabilidad. Diversificación.", c: "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" },
                      { v: "agresivo", l: "Agresivo", d: "Priorizo rentabilidad. Acepto volatilidad alta y posibles pérdidas temporales.", c: "text-red-400 border-red-400/50 bg-red-500/10" },
                    ].map(r => (
                      <button key={r.v} onClick={() => update("risk_tolerance", r.v)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          form.risk_tolerance === r.v ? r.c : 'bg-[#141b2d] border-[#2a3444] text-gray-400'
                        }`} data-testid={`risk-${r.v}`}>
                        <span className="font-bold text-base">{r.l}</span>
                        <p className="text-xs mt-1 opacity-80">{r.d}</p>
                      </button>
                    ))}
                    <div className="mt-4">
                      <p className="text-sm text-gray-400 mb-2">Experiencia en inversiones</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[{v:"ninguna",l:"Ninguna"},{v:"basica",l:"Básica (ahorro)"},{v:"intermedia",l:"Intermedia (fondos/CDTs)"},{v:"avanzada",l:"Avanzada (acciones/cripto)"}].map(e => (
                          <button key={e.v} onClick={() => update("investment_experience", e.v)}
                            className={`p-2.5 rounded-lg border text-xs transition-all ${
                              form.investment_experience === e.v ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-white' : 'bg-[#141b2d] border-[#2a3444] text-gray-500'
                            }`}>{e.l}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#2a3444]">
                  <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="text-gray-400">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                  </Button>
                  {step < STEPS.length - 1 ? (
                    <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="btn-gold" data-testid="next-step">
                      Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={saving || !canNext()} className="btn-gold" data-testid="submit-survey">
                      {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Ver mi Diagnóstico
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Detail Panel */}
          <div className="lg:col-span-2">
            <Card className="bg-[#1a2332] border-[#2a3444] sticky top-4">
              <CardContent className="p-6">
                {step === 1 && selectedKnowledge ? (
                  <div>
                    <Target className="w-8 h-8 text-[#D4AF37] mb-3" />
                    <h3 className="text-white font-bold mb-2">{selectedKnowledge.label}</h3>
                    <p className="text-sm text-gray-400">{selectedKnowledge.desc}</p>
                    {(form.knowledge_details || []).length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">Áreas seleccionadas:</p>
                        <div className="flex flex-wrap gap-1">
                          {form.knowledge_details.map(a => (
                            <span key={a} className="text-[9px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded">{a}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : step === 0 ? (
                  <div>
                    <TrendingUp className="w-8 h-8 text-[#D4AF37] mb-3" />
                    <h3 className="text-white font-bold mb-2">¿Por qué estos datos?</h3>
                    <p className="text-sm text-gray-400">Esta información nos permite calcular tu ratio de ahorro, nivel de endeudamiento y capacidad de inversión para darte un diagnóstico personalizado.</p>
                  </div>
                ) : step === 4 ? (
                  <div>
                    <Shield className="w-8 h-8 text-[#D4AF37] mb-3" />
                    <h3 className="text-white font-bold mb-2">Perfil de Riesgo</h3>
                    <p className="text-sm text-gray-400">Tu tolerancia al riesgo determina qué tipo de inversiones y portafolios son más adecuados para ti. No hay respuesta correcta, cada perfil tiene sus ventajas.</p>
                  </div>
                ) : (
                  <div>
                    <Target className="w-8 h-8 text-[#D4AF37] mb-3" />
                    <h3 className="text-white font-bold mb-2">Tu Perfil</h3>
                    <p className="text-sm text-gray-400">Al completar esta evaluación recibirás un diagnóstico financiero personalizado con tu nivel y perfil de riesgo.</p>
                    {form.main_financial_goal && (
                      <div className="mt-3 p-2 bg-[#141b2d] rounded text-xs text-gray-400">
                        <span className="text-[#D4AF37]">Objetivo:</span> {GOALS.find(g => g.value === form.main_financial_goal)?.label}
                        {form.goal_timeframe && <span className="ml-1">— {form.goal_timeframe.replace("_", " ")}</span>}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
